// @ts-nocheck
import React, {
    useEffect,
    useRef,
    useState
} from 'react';


import {
    URL,
    URL_FRONT
} from '../../service/Service';

import { useI18n } from '../../i18n/I18nContext';


/*
 * A página externa do cartão ainda faz pedidos internos depois de o
 * documento do iframe começar a carregar. Como o conteúdo é cross-origin,
 * o euForms não consegue observar diretamente quando esses pedidos acabam.
 *
 * Nos testes do IT-1220, o carregamento externo ficou perto dos 3 segundos.
 * Mantemos por isso o estado visual de carregamento durante, no mínimo,
 * este intervalo a partir do momento em que o iframe é iniciado.
 */
const MIN_IFRAME_LOADING_MS = 3000;


const CC = ({
                value,
                formReplies,
                fixedValue,
                itemPurchases,
                donations,
                email,
                onEmailChange
            }) => {

    const { language, t } = useI18n();

    const iframeRef =
        useRef(null);

    const iframeStartedAtRef =
        useRef(0);

    const iframeReadyTimerRef =
        useRef(null);


    const [error, setError] =
        useState('');

    const [isChecked, setIsChecked] =
        useState(false);

    const [isSubmitting, setIsSubmitting] =
        useState(false);

    const [paymentUrl, setPaymentUrl] =
        useState('');

    const [paymentSucceeded, setPaymentSucceeded] =
        useState(false);

    const [iframeLoaded, setIframeLoaded] =
        useState(false);


    /*
     * URL pública do frontend.
     */
    const publicFrontendUrl = (
        process.env.REACT_APP_PUBLIC_FRONTEND_URL
        ||
        URL_FRONT
        ||
        window.location.origin
    ).replace(/\/+$/, '');


    /*
     * Origem permitida para mensagens
     * provenientes do callback.
     */
    let callbackOrigin = '';

    try {

        callbackOrigin =
            new URL(
                publicFrontendUrl
            ).origin;

    } catch (_error) {

        callbackOrigin = '';

    }


    const paymentErrorMessage =
        'Não foi possível iniciar o pagamento com Cartão de Crédito. Tente novamente ou escolha outro método.';


    const paymentUnavailableMessage =
        'Cartão de Crédito não está disponível para este formulário. Por favor, selecione outro método de pagamento.';


    const paymentFailedMessage =
        'O pagamento não foi concluído. Pode tentar novamente ou escolher outro método de pagamento.';


    /*
     * Limpar qualquer timer pendente quando o componente é desmontado.
     */
    useEffect(() => {

        return () => {

            if (iframeReadyTimerRef.current) {

                window.clearTimeout(
                    iframeReadyTimerRef.current
                );

                iframeReadyTimerRef.current = null;
            }

        };

    }, []);


    /*
     * =====================================================
     * MENSAGENS DO IFRAME
     * =====================================================
     *
     * O ficheiro payment-iframe-callback.html
     * comunica com esta view através de postMessage.
     */
    useEffect(() => {

        const handleIframeMessage = (event) => {

            /*
             * Apenas aceitar mensagens provenientes
             * do iframe desta view.
             */
            if (
                iframeRef.current
                &&
                event.source !==
                iframeRef.current.contentWindow
            ) {
                return;
            }


            /*
             * Validar também a origem pública configurada.
             */
            if (
                callbackOrigin
                &&
                event.origin !== callbackOrigin
            ) {
                return;
            }


            const message =
                event.data;


            if (
                !message
                ||
                message.type !==
                'EUFORMS_PAYMENT_IFRAME'
            ) {
                return;
            }


            /*
             * Garantir que o callback pertence
             * ao formulário atual.
             */
            if (
                message.formId
                &&
                String(message.formId) !==
                String(formReplies.formID)
            ) {
                return;
            }


            /*
             * =================================================
             * VOLTAR
             * =================================================
             *
             * Fecha o iframe e volta aos dados
             * do Cartão de Crédito.
             */
            if (message.action === 'back') {

                if (iframeReadyTimerRef.current) {

                    window.clearTimeout(
                        iframeReadyTimerRef.current
                    );

                    iframeReadyTimerRef.current = null;
                }

                setPaymentUrl('');

                setPaymentSucceeded(false);

                setIframeLoaded(false);

                setError('');

                setIsSubmitting(false);

                return;
            }


            /*
             * =================================================
             * FALHA / CANCELAMENTO
             * =================================================
             */
            if (message.action === 'fail') {

                if (iframeReadyTimerRef.current) {

                    window.clearTimeout(
                        iframeReadyTimerRef.current
                    );

                    iframeReadyTimerRef.current = null;
                }

                setPaymentUrl('');

                setPaymentSucceeded(false);

                setIframeLoaded(false);

                setError(
                    paymentFailedMessage
                );

                setIsSubmitting(false);

                return;
            }


            /*
             * =================================================
             * SUCESSO
             * =================================================
             *
             * IT-1220:
             * o utilizador permanece no formulário.
             * O iframe fecha e o sucesso é apresentado
             * dentro do próprio card do Cartão de Crédito.
             */
            if (message.action === 'success') {

                if (iframeReadyTimerRef.current) {

                    window.clearTimeout(
                        iframeReadyTimerRef.current
                    );

                    iframeReadyTimerRef.current = null;
                }

                setPaymentUrl('');

                setPaymentSucceeded(true);

                setIframeLoaded(false);

                setError('');

                setIsSubmitting(false);

                return;
            }

        };


        window.addEventListener(
            'message',
            handleIframeMessage
        );


        return () => {

            window.removeEventListener(
                'message',
                handleIframeMessage
            );

        };

    }, [
        callbackOrigin,
        formReplies.formID,
        paymentFailedMessage
    ]);


    /*
     * Identificar método indisponível.
     */
    const isPaymentMethodUnavailable = (
        responseStatus,
        responseText,
        result
    ) => {

        if (responseStatus === 503) {
            return true;
        }


        const rawMessage = [
            responseText,
            result?.message,
            result?.error,
            result?.detail
        ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();


        return (
            rawMessage.includes(
                'método de pagamento selecionado não está disponível'
            )
            ||
            rawMessage.includes(
                'metodo de pagamento selecionado nao esta disponivel'
            )
            ||
            rawMessage.includes(
                'método de pagamento não está disponível'
            )
            ||
            rawMessage.includes(
                'metodo de pagamento nao esta disponivel'
            )
            ||
            rawMessage.includes(
                'payment method unavailable'
            )
            ||
            rawMessage.includes(
                'paymentmethodunavailable'
            )
        );
    };


    const handlePayment = async () => {

        if (
            isSubmitting
            ||
            !isChecked
        ) {
            return;
        }


        const normalizedEmail =
            String(email || '').trim();


        if (!normalizedEmail) {

            setError(
                'Introduza o seu email.'
            );

            return;
        }


        setIsSubmitting(true);

        if (iframeReadyTimerRef.current) {

            window.clearTimeout(
                iframeReadyTimerRef.current
            );

            iframeReadyTimerRef.current = null;
        }

        iframeStartedAtRef.current = 0;

        setPaymentSucceeded(false);

        setIframeLoaded(false);

        setError('');


        const formId =
            encodeURIComponent(
                formReplies.formID
            );


        /*
         * =================================================
         * CALLBACKS DO IFRAME
         * =================================================
         */

        const callbackBase =
            `${publicFrontendUrl}/payment-iframe-callback.html`;


        const successUrl =
            `${callbackBase}?action=success&method=cc&formId=${formId}`;


        const failUrl =
            `${callbackBase}?action=fail&method=cc&formId=${formId}`;


        const backUrl =
            `${callbackBase}?action=back&method=cc&formId=${formId}`;


        try {

            const body = {

                value,

                successUrl,

                failUrl,

                backUrl,

                email:
                normalizedEmail,

                fixedValue,

                itemPurchases,

                donations,

                formID:
                formReplies.formID,

                replyFields:
                formReplies.replyFields,

                language,

                ip:
                    formReplies?.ip || ''
            };


            const response =
                await fetch(
                    `${URL}/api/payment/cc`,
                    {
                        method: 'POST',

                        headers: {
                            accept:
                                'application/json',

                            'content-type':
                                'application/json'
                        },

                        body:
                            JSON.stringify(body)
                    }
                );


            const responseText =
                await response.text();


            let result = {};


            if (responseText) {

                try {

                    result =
                        JSON.parse(
                            responseText
                        );

                } catch (_error) {

                    result = {};

                }

            }


            if (
                !response.ok
                &&
                isPaymentMethodUnavailable(
                    response.status,
                    responseText,
                    result
                )
            ) {

                setError(
                    paymentUnavailableMessage
                );

                return;
            }


            if (!response.ok) {

                setError(
                    paymentErrorMessage
                );

                return;
            }


            const redirectUrl =
                result?.urlCc
                ||
                result?.redirectUrl;


            if (!redirectUrl) {

                setError(
                    paymentErrorMessage
                );

                return;
            }


            /*
             * IT-1220:
             * pagamento dentro do formulário.
             */
            setIframeLoaded(false);

            /*
             * A partir daqui o browser começa efetivamente a carregar
             * a página externa do cartão.
             */
            iframeStartedAtRef.current =
                performance.now();

            setPaymentUrl(
                redirectUrl
            );


        } catch (_error) {

            setError(
                paymentErrorMessage
            );

        } finally {

            setIsSubmitting(false);

        }

    };


    /*
     * =====================================================
     * SUCESSO DENTRO DO FORMULÁRIO
     * =====================================================
     */
    if (paymentSucceeded) {

        return (

            <div className="mt-5">

                <div
                    role="status"
                    aria-live="polite"
                    className="
                        rounded-2xl
                        border
                        p-6
                        text-center
                        shadow-sm
                        sm:p-8
                    "
                    style={{
                        background:
                            'var(--form-soft)',

                        borderColor:
                            'var(--form-border)',

                        color:
                            'var(--form-text)'
                    }}
                >

                    <div
                        className="
                            mx-auto
                            flex
                            h-16
                            w-16
                            items-center
                            justify-center
                            rounded-full
                            border-2
                        "
                        style={{
                            borderColor:
                                'var(--form-accent)',

                            color:
                                'var(--form-accent)',

                            background:
                                'var(--form-surface)'
                        }}
                    >

                        <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="h-9 w-9"
                            aria-hidden="true"
                        >
                            <path d="M20 6 9 17l-5-5" />
                        </svg>

                    </div>


                    <h3
                        className="
                            mt-5
                            text-xl
                            font-bold
                            sm:text-2xl
                        "
                        style={{
                            color:
                                'var(--form-text)'
                        }}
                    >
                        O seu pagamento foi processado com sucesso!
                    </h3>


                    <p
                        className="mt-2 text-sm leading-6"
                        style={{
                            color:
                                'var(--form-muted)'
                        }}
                    >
                        Pagamento concluído com Cartão de Crédito.
                    </p>


                    <div
                        className="
                            mx-auto
                            mt-5
                            inline-flex
                            items-center
                            gap-2
                            rounded-xl
                            border
                            px-4
                            py-2
                        "
                        style={{
                            background:
                                'var(--form-surface)',

                            borderColor:
                                'var(--form-border)',

                            color:
                                'var(--form-text)'
                        }}
                    >

                        <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="h-5 w-5"
                            aria-hidden="true"
                        >
                            <rect
                                x="3"
                                y="5"
                                width="18"
                                height="14"
                                rx="2"
                            />
                            <path d="M3 10h18" />
                        </svg>

                        <span className="text-sm font-semibold">
                            Cartão de Crédito
                        </span>

                    </div>

                </div>

            </div>

        );

    }


    /*
     * =====================================================
     * A PREPARAR PAGAMENTO
     * =====================================================
     *
     * Mostrado imediatamente depois de clicar em
     * "Continuar para pagamento", enquanto o backend
     * obtém o redirectUrl junto da Eupago.
     */
    if (isSubmitting && !paymentUrl) {

        return (

            <div className="mt-5">

                <div
                    role="status"
                    aria-live="polite"
                    className="
                        rounded-2xl
                        border
                        p-6
                        text-center
                        shadow-sm
                        sm:p-8
                    "
                    style={{
                        background:
                            'var(--form-soft)',

                        borderColor:
                            'var(--form-border)',

                        color:
                            'var(--form-text)'
                    }}
                >

                    <div
                        className="
                            mx-auto
                            h-12
                            w-12
                            animate-spin
                            rounded-full
                            border-4
                        "
                        style={{
                            borderColor:
                                'var(--form-border)',

                            borderTopColor:
                                'var(--form-accent)'
                        }}
                        aria-hidden="true"
                    />


                    <h3
                        className="mt-5 text-lg font-bold sm:text-xl"
                        style={{
                            color:
                                'var(--form-text)'
                        }}
                    >
                        A preparar pagamento seguro
                    </h3>


                    <p
                        className="mt-2 text-sm leading-6"
                        style={{
                            color:
                                'var(--form-muted)'
                        }}
                    >
                        Estamos a ligar ao ambiente seguro da Eupago.
                        Aguarde alguns segundos.
                    </p>


                    <div
                        className="
                            mx-auto
                            mt-5
                            inline-flex
                            items-center
                            gap-2
                            rounded-xl
                            border
                            px-4
                            py-2
                        "
                        style={{
                            background:
                                'var(--form-surface)',

                            borderColor:
                                'var(--form-border)',

                            color:
                                'var(--form-text)'
                        }}
                    >

                        <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="h-5 w-5"
                            aria-hidden="true"
                        >
                            <rect
                                x="3"
                                y="5"
                                width="18"
                                height="14"
                                rx="2"
                            />
                            <path d="M3 10h18" />
                        </svg>

                        <span className="text-sm font-semibold">
                            Cartão de Crédito
                        </span>

                    </div>

                </div>

            </div>

        );

    }


    /*
     * =====================================================
     * IFRAME
     * =====================================================
     */
    if (paymentUrl) {

        return (

            <div className="mt-5">


                <div
                    className="
                        mb-4
                        flex
                        items-start
                        gap-3
                        rounded-2xl
                        border
                        px-4
                        py-4
                        sm:px-5
                    "

                    style={{
                        background:
                            'var(--form-soft)',

                        borderColor:
                            'var(--form-border)',

                        color:
                            'var(--form-text)'
                    }}
                >


                    <div
                        className="
                            flex
                            h-10
                            w-10
                            shrink-0
                            items-center
                            justify-center
                            rounded-xl
                        "

                        style={{
                            background:
                                'var(--form-surface)',

                            border:
                                '1px solid var(--form-border)',

                            color:
                                'var(--form-accent)'
                        }}
                    >

                        <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="h-5 w-5"
                            aria-hidden="true"
                        >

                            <rect
                                x="5"
                                y="10"
                                width="14"
                                height="10"
                                rx="2"
                            />

                            <path
                                d="M8 10V7a4 4 0 0 1 8 0v3"
                            />

                        </svg>

                    </div>


                    <div className="min-w-0">

                        <div className="flex flex-wrap items-center gap-2">

                            <p
                                className="text-sm font-bold sm:text-base"

                                style={{
                                    color:
                                        'var(--form-text)'
                                }}
                            >
                                {t('Pagamento seguro')}
                            </p>


                            <span
                                className="hidden text-sm sm:inline"

                                style={{
                                    color:
                                        'var(--form-muted)'
                                }}
                            >
                                ·
                            </span>


                            <span
                                className="text-sm font-semibold"

                                style={{
                                    color:
                                        'var(--form-accent)'
                                }}
                            >
                                {t('Cartão de Crédito')}
                            </span>

                        </div>


                        <p
                            className="mt-1 text-sm leading-5"

                            style={{
                                color:
                                    'var(--form-muted)'
                            }}
                        >
                            {t('Introduza os dados do cartão no ambiente seguro da Eupago para concluir o pagamento.')}
                        </p>

                    </div>

                </div>


                <div
                    className="
                        relative
                        w-full
                        overflow-hidden
                        rounded-2xl
                        border
                        shadow-sm
                    "

                    style={{
                        background:
                            '#ffffff',

                        borderColor:
                            'var(--form-border)'
                    }}
                >

                    {!iframeLoaded && (

                        <div
                            className="
                                absolute
                                inset-0
                                z-10
                                flex
                                min-h-[620px]
                                flex-col
                                items-center
                                justify-center
                                px-6
                                text-center
                            "
                            style={{
                                background:
                                    '#ffffff',

                                color:
                                    'var(--form-text)'
                            }}
                            role="status"
                            aria-live="polite"
                        >

                            <div
                                className="
                                    h-11
                                    w-11
                                    animate-spin
                                    rounded-full
                                    border-4
                                "
                                style={{
                                    borderColor:
                                        'var(--form-border)',

                                    borderTopColor:
                                        'var(--form-accent)'
                                }}
                                aria-hidden="true"
                            />


                            <p
                                className="mt-4 font-semibold"
                                style={{
                                    color:
                                        'var(--form-text)'
                                }}
                            >
                                A carregar formulário do cartão...
                            </p>


                            <p
                                className="mt-1 text-sm"
                                style={{
                                    color:
                                        'var(--form-muted)'
                                }}
                            >
                                Estamos a concluir a ligação ao ambiente seguro da Eupago.
                            </p>

                        </div>

                    )}


                    <iframe
                        ref={iframeRef}

                        src={paymentUrl}

                        title={`${t('Pagamento seguro')} · ${t('Cartão de Crédito')}`}

                        allow="payment"

                        onLoad={() => {

                            const startedAt =
                                iframeStartedAtRef.current;

                            const elapsed =
                                startedAt
                                    ? performance.now() - startedAt
                                    : MIN_IFRAME_LOADING_MS;

                            const remaining =
                                Math.max(
                                    0,
                                    MIN_IFRAME_LOADING_MS - elapsed
                                );

                            if (iframeReadyTimerRef.current) {

                                window.clearTimeout(
                                    iframeReadyTimerRef.current
                                );
                            }

                            iframeReadyTimerRef.current =
                                window.setTimeout(
                                    () => {

                                        setIframeLoaded(true);

                                        iframeReadyTimerRef.current =
                                            null;

                                    },
                                    remaining
                                );
                        }}

                        className="
                            block
                            w-full
                            border-0
                            bg-white
                        "

                        style={{
                            width:
                                '100%',

                            height:
                                '620px',

                            minHeight:
                                '620px',

                            opacity:
                                iframeLoaded ? 1 : 0,

                            transition:
                                'opacity 180ms ease'
                        }}
                    />

                </div>


                <div
                    className="
                        mt-3
                        flex
                        items-center
                        justify-center
                        gap-1.5
                        text-xs
                    "

                    style={{
                        color:
                            'var(--form-muted)'
                    }}
                >

                    <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="h-3.5 w-3.5"
                        aria-hidden="true"
                    >

                        <rect
                            x="5"
                            y="10"
                            width="14"
                            height="10"
                            rx="2"
                        />

                        <path
                            d="M8 10V7a4 4 0 0 1 8 0v3"
                        />

                    </svg>


                    <span>
                        Pagamento processado em ambiente seguro Eupago
                    </span>

                </div>

            </div>

        );

    }


    /*
     * =====================================================
     * ANTES DO PAGAMENTO
     * =====================================================
     */
    return (

        <div className="mt-5">


            <label
                htmlFor="email-credit-card"
                className="mb-2 block text-sm font-bold"

                style={{
                    color:
                        'var(--form-text)'
                }}
            >
                Email
            </label>


            <input
                id="email-credit-card"
                type="email"
                name="email-credit-card"
                autoComplete="email"
                placeholder="Insira o seu Email"
                required

                value={email || ''}

                onChange={onEmailChange}

                disabled={isSubmitting}

                className="
                    block
                    w-full
                    rounded-xl
                    border
                    border-gray-300
                    bg-white
                    p-2.5
                    text-sm
                    text-gray-900
                    outline-none
                    transition
                    focus:border-green-cl
                    focus:ring-2
                    focus:ring-green-cl/20
                    disabled:cursor-not-allowed
                    disabled:opacity-60
                "

                style={{
                    backgroundColor:
                        '#ffffff',

                    color:
                        '#111827'
                }}
            />


            <label
                htmlFor="verify-checkbox-credit-card"

                className="
                    mt-6
                    flex
                    cursor-pointer
                    items-start
                    gap-3
                    rounded-xl
                    border
                    p-4
                    text-sm
                "

                style={{
                    color:
                        'var(--form-text)',

                    borderColor:
                        'var(--form-border)',

                    background:
                        'var(--form-surface)'
                }}
            >

                <input
                    id="verify-checkbox-credit-card"

                    type="checkbox"

                    checked={isChecked}

                    disabled={isSubmitting}

                    onChange={(event) => {

                        setIsChecked(
                            event.target.checked
                        );

                        if (error) {
                            setError('');
                        }

                    }}

                    className="
                        mt-0.5
                        h-4
                        w-4
                        shrink-0
                        rounded
                        border-gray-300
                        text-green-cl
                        focus:ring-green-cl
                        disabled:cursor-not-allowed
                        disabled:opacity-60
                    "
                />


                <span className="leading-relaxed">
                    Tomei conhecimento que a informação constante deste formulário é guardada e tratada pela Eupago e dou consentimento para tal.
                </span>

            </label>


            {error && (

                <div
                    role="alert"

                    className="
                        mt-4
                        rounded-xl
                        border
                        border-red-200
                        bg-red-50
                        p-4
                    "
                >

                    <p className="text-sm font-semibold text-red-700">
                        {error}
                    </p>

                </div>

            )}


            <button
                type="button"

                onClick={handlePayment}

                disabled={
                    !isChecked
                    ||
                    isSubmitting
                    ||
                    !String(email || '').trim()
                }

                className="
                    mt-6
                    inline-flex
                    w-full
                    items-center
                    justify-center
                    rounded-xl
                    bg-green-cl
                    px-5
                    py-3
                    font-semibold
                    text-white
                    transition
                    hover:bg-opacity-90
                    disabled:cursor-not-allowed
                    disabled:opacity-50
                "
            >

                {
                    isSubmitting
                        ? 'A processar...'
                        : 'Continuar para pagamento'
                }

            </button>

        </div>

    );

};


export default CC;
